<template>
  <div class="card pkce-connection">
    <div class="card-body">
      <img src="/img/login-banner.png" alt="" class="pkce-mascot">
      <h5 class="card-title">
        Connexion à MPG
      </h5>
      <p class="alert alert-info">
        Vous vous connectez directement sur la page officielle MPG. Vos identifiants ne
        transitent jamais par MPG-Calculator ; seul un code temporaire sert à obtenir
        votre accès.
      </p>

      <ol class="steps" data-test="steps">
        <li class="step">
          <span class="step__num">1</span>
          <div class="step__body">
            <p class="step__text">
              Cliquez sur ce bouton : une page officielle MPG s'ouvre dans un nouvel onglet.
            </p>
            <a
              data-test="login-btn"
              class="btn btn-success"
              :href="prepared?.url"
              target="_blank"
              rel="noopener"
              @click="onLoginClick"
            >
              Se connecter à MPG
            </a>
          </div>
        </li>

        <li class="step">
          <span class="step__num">2</span>
          <div class="step__body">
            <p class="step__text">
              <strong>Connectez-vous sur la page MPG</strong> qui vient de s'ouvrir, comme
              d'habitude sur leur application.
            </p>
            <svg
              class="step__illus"
              viewBox="0 0 220 110"
              role="img"
              aria-label="Un nouvel onglet s'ouvre sur la page de connexion MPG"
            >
              <rect
                x="6"
                y="10"
                width="150"
                height="92"
                rx="6"
                class="illus-win"
              />
              <rect
                x="6"
                y="10"
                width="150"
                height="16"
                rx="6"
                class="illus-bar"
              />
              <circle
                cx="16"
                cy="18"
                r="2.4"
                class="illus-dot"
              />
              <circle
                cx="24"
                cy="18"
                r="2.4"
                class="illus-dot"
              />
              <circle
                cx="32"
                cy="18"
                r="2.4"
                class="illus-dot"
              />
              <rect
                x="40"
                y="40"
                width="82"
                height="9"
                rx="4.5"
                class="illus-line"
              />
              <rect
                x="40"
                y="56"
                width="110"
                height="9"
                rx="4.5"
                class="illus-line"
              />
              <rect
                x="40"
                y="76"
                width="54"
                height="14"
                rx="7"
                class="illus-cta"
              />
              <rect
                x="96"
                y="34"
                width="118"
                height="70"
                rx="8"
                class="illus-win illus-front"
              />
              <rect
                x="96"
                y="34"
                width="118"
                height="18"
                rx="8"
                class="illus-bar illus-front-bar"
              />
              <text x="108" y="46" class="illus-label">MPG</text>
              <rect
                x="112"
                y="66"
                width="86"
                height="8"
                rx="4"
                class="illus-line"
              />
              <rect
                x="112"
                y="80"
                width="64"
                height="8"
                rx="4"
                class="illus-line"
              />
            </svg>
          </div>
        </li>

        <li class="step">
          <span class="step__num">3</span>
          <div class="step__body">
            <p class="step__text">
              Une fois connecté, vous arrivez sur <code>mpg.football</code>.
              <strong>Copiez toute l'adresse</strong> affichée dans la barre d'adresse du
              navigateur (elle contient un code).
            </p>
            <p class="alert alert-warning step__warning" role="alert">
              ⚠️ Si une page de connexion MPG réapparaît, <strong>ne vous reconnectez pas</strong> :
              l'adresse où vous venez d'arriver contient déjà votre code. Il suffit de la copier.
            </p>
            <svg
              class="step__illus step__illus--wide"
              viewBox="0 0 360 86"
              role="img"
              aria-label="Barre d'adresse du navigateur : copiez l'adresse entière commençant par https://mpg.football/?code="
            >
              <rect
                x="6"
                y="14"
                width="348"
                height="34"
                rx="17"
                class="illus-addr"
              />
              <path d="M22 31 a5 5 0 0 1 10 0 v3 h-10 z" class="illus-lock" />
              <rect
                x="20"
                y="31"
                width="14"
                height="10"
                rx="2"
                class="illus-lock"
              />
              <text x="44" y="36" class="illus-url">https://mpg.football/?code=<tspan class="illus-url-code">a1b2c3…</tspan></text>
              <rect
                x="40"
                y="20"
                width="250"
                height="22"
                rx="6"
                class="illus-highlight"
              />
              <path d="M150 52 l0 12 M150 64 l-5 -5 M150 64 l5 -5" class="illus-arrow" />
              <text x="160" y="72" class="illus-note">copiez tout, du « https » jusqu'à la fin</text>
            </svg>
          </div>
        </li>

        <li class="step">
          <span class="step__num">4</span>
          <div class="step__body">
            <p class="step__text">
              <strong>Collez l'adresse ci-dessous</strong> et validez. C'est tout !
            </p>
            <input
              v-model="callbackInput"
              data-test="code-input"
              aria-label="Adresse de retour ou code MPG"
              class="form-control"
              placeholder="https://mpg.football/?code=..."
              @keyup.enter="validate"
            >
            <button data-test="validate-btn" class="btn btn-primary mt-2" @click="validate">
              Valider
            </button>
          </div>
        </li>
      </ol>

      <div v-if="message" :class="`alert mt-3 alert-${messageKind === 'error' ? 'danger' : 'warning'}`" role="alert">
        {{ message }}
      </div>

      <div class="mt-3 text-center">
        <a href="#" @click.prevent="showAdvancedLogin = true">Connexion impossible ?</a>
      </div>
      <advanced-login-modal v-model="showAdvancedLogin" />
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from "vue";
import AdvancedLoginModal from "@/components/login/AdvancedLoginModal.vue";
import { useMPG } from "@/use/useMPG";

const { prepareLogin, beginLogin, completeLogin } = useMPG();
const callbackInput = ref("");
const message = ref("");
const messageKind = ref("warning");
const showAdvancedLogin = ref(false);
const prepared = ref(null);

const showMessage = (text, kind) => {
  message.value = text;
  messageKind.value = kind;
};

// On calcule l'URL d'autorisation (PKCE) dès le montage : le clic reste alors
// synchrone et l'ouverture du nouvel onglet n'est pas bloquée sur mobile.
onMounted(async () => {
  try {
    prepared.value = await prepareLogin();
  } catch (e) {
    showMessage(e.message ?? "Impossible de préparer la connexion.", "error");
  }
});

// Clic synchrone : on mémorise le verifier puis on laisse le lien <a> s'ouvrir.
const onLoginClick = (event) => {
  if (! prepared.value) {
    event.preventDefault();
    showMessage("Préparation de la connexion en cours, réessayez dans un instant.", "warning");
    return;
  }
  message.value = "";
  beginLogin(prepared.value);
};

const validate = async () => {
  message.value = "";
  if (! callbackInput.value) {
    showMessage("Collez le code (ou l'URL de retour).", "warning");
    return;
  }
  try {
    await completeLogin(callbackInput.value);
  } catch (e) {
    showMessage(e.message ?? "Connexion impossible.", "warning");
  }
};
</script>

<style scoped lang="scss">
.pkce-connection {
  max-width: 600px;
}

.pkce-mascot {
  display: block;
  width: 96px;
  height: 96px;
  margin: 0 auto 0.5rem;
  border-radius: 20px;
  object-fit: cover;
  object-position: 50% 20%;
}

.steps {
  list-style: none;
  margin: 0;
  padding: 0;
}

.step {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;

  &:not(:last-child) {
    padding-bottom: 1.1rem;
    margin-bottom: 1.1rem;
    border-bottom: 1px solid #e9ecef;
  }
}

.step__num {
  flex: 0 0 auto;
  width: 1.9rem;
  height: 1.9rem;
  border-radius: 50%;
  background: #198754;
  color: #fff;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.step__body {
  flex: 1 1 auto;
  min-width: 0;
}

.step__text {
  margin-bottom: 0.6rem;
}

.step__warning {
  margin: 0.6rem 0;
  padding: 0.5rem 0.75rem;
  font-size: 0.9rem;
}

.step__illus {
  width: 100%;
  max-width: 240px;
  height: auto;

  &--wide {
    max-width: 360px;
  }
}

/* Couleurs des illustrations */
.illus-win { fill: #fff; stroke: #ced4da; stroke-width: 1.5; }
.illus-front { filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.18)); }
.illus-bar { fill: #f1f3f5; }
.illus-front-bar { fill: #198754; }
.illus-dot { fill: #ced4da; }
.illus-line { fill: #dee2e6; }
.illus-cta { fill: #198754; }
.illus-label { fill: #fff; font-size: 10px; font-weight: 700; }
.illus-addr { fill: #fff; stroke: #ced4da; stroke-width: 1.5; }
.illus-lock { fill: #868e96; }
.illus-url { fill: #495057; font-size: 11px; font-family: monospace; }
.illus-url-code { fill: #198754; font-weight: 700; }
.illus-highlight { fill: #198754; opacity: 0.15; }
.illus-arrow { stroke: #198754; stroke-width: 2; fill: none; stroke-linecap: round; stroke-linejoin: round; }
.illus-note { fill: #198754; font-size: 10px; font-weight: 600; }
</style>